'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import api from '@/lib/api';
import { UserTier, type User } from '@xue-ying-yu/shared';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>(null!);

export class NotAdminError extends Error {
  constructor() {
    super('该账号无管理端访问权限');
    this.name = 'NotAdminError';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // 先尝试 refresh(access token 过期但 refresh_token 还有效 → 静默续期)
    // refresh 失败才认定未登录
    api
      .post('/auth/refresh')
      .catch(() => null)
      .finally(() => {
        api
          .get('/auth/me')
          .then((res) => {
            // 防御性检查:cookie 还存活但用户已被降级/换号,当成未登录
            if (res.data?.tier !== UserTier.ADMIN) {
              setUser(null);
            } else {
              setUser(res.data);
            }
          })
          .catch(() => {
            setUser(null);
          })
          .finally(() => {
            setLoading(false);
          });
      });
  }, []);

  const login = async (identifier: string, password: string) => {
    const res = await api.post('/auth/login', { identifier, password });
    if (res.data.user.tier !== UserTier.ADMIN) {
      // 不是管理员:立即清掉服务端 session,避免 refresh token 续期
      // (后端 AdminGuard 已经拦了所有 admin API,这里只是清干净不让前端状态残留)
      await api.post('/auth/logout').catch(() => {});
      setUser(null);
      throw new NotAdminError();
    }
    setUser(res.data.user);
  };

  const logout = useCallback(() => {
    api.post('/auth/logout').catch(() => {});
    setUser(null);
    router.push('/login');
  }, [router]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
